import { useState, useEffect, useRef, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Card, CardContent } from "@/components/ui/card";
import { SectionSaveStatus } from "./SectionSaveStatus";
import type { Client } from "@shared/schema";
import { FONT, WARM } from "@shared/brand";

interface ExecutorMessageData {
  value: string | null;
  fieldId: string | null;
  sectionId: string | null;
  categoryId: string | null;
}

interface MessageToExecutorCardProps {
  clientData: Client;
  readOnly?: boolean;
}

export function MessageToExecutorCard({ clientData, readOnly }: MessageToExecutorCardProps) {
  const queryClient = useQueryClient();
  const [localValue, setLocalValue] = useState("");
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const initialised = useRef(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mutateRef = useRef<(value: string) => void>(() => {});
  // Stable ref for field IDs — bypasses stale-closure issues in mutationFn
  const fieldMetaRef = useRef<{ fieldId: string; sectionId: string; categoryId: string } | null>(null);

  const { data } = useQuery<ExecutorMessageData>({
    queryKey: ["/api/clients", clientData.id, "executor-message"],
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/clients/${clientData.id}/executor-message`);
      return res.json();
    },
  });

  // Populate local state and field metadata once on first data load
  useEffect(() => {
    if (data && !initialised.current) {
      initialised.current = true;
      setLocalValue(data.value ?? "");
    }
    if (data?.fieldId && data?.sectionId && data?.categoryId) {
      fieldMetaRef.current = {
        fieldId: data.fieldId,
        sectionId: data.sectionId,
        categoryId: data.categoryId,
      };
    }
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: async (value: string) => {
      const meta = fieldMetaRef.current;
      if (!meta) throw new Error("Field metadata not loaded — please refresh the page");
      const res = await apiRequest("POST", `/api/clients/${clientData.id}/generic-entries`, {
        categoryId: meta.categoryId,
        sectionId: meta.sectionId,
        fieldId: meta.fieldId,
        value,
      });
      return res.json();
    },
    onSuccess: () => {
      setLastSavedAt(new Date());
      queryClient.invalidateQueries({
        queryKey: ["/api/clients", clientData.id, "executor-message"],
      });
    },
  });

  // Keep mutate ref current so debounce callbacks always call the latest version
  mutateRef.current = saveMutation.mutate;

  const triggerSave = useCallback((value: string) => {
    mutateRef.current(value);
  }, []);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (readOnly) return;
    const value = e.target.value;
    setLocalValue(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => triggerSave(value), 1000);
  }, [readOnly, triggerSave]);

  const handleBlur = useCallback((e: React.FocusEvent<HTMLTextAreaElement>) => {
    if (readOnly || !fieldMetaRef.current) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    triggerSave(e.target.value);
  }, [readOnly, triggerSave]);

  const descriptionId = "message-to-executor-desc";

  return (
    <Card className="rounded-xl">
      <CardContent className="p-6">
        <h2
          style={{
            fontFamily: FONT.serif,
            fontSize: 20,
            fontWeight: 500,
            color: "#2C2C2C",
            marginBottom: 6,
          }}
        >
          A Message to Your Executor
        </h2>
        <p
          id={descriptionId}
          style={{
            fontFamily: FONT.sans,
            fontSize: 13,
            color: WARM.taupe,
            lineHeight: 1.5,
            marginBottom: 16,
          }}
        >
          A personal letter to the people settling your estate. Think of it as a hand on the shoulder — not legal instructions.
        </p>

        <label htmlFor="message-to-executor" className="sr-only">
          A Message to Your Executor
        </label>
        <textarea
          id="message-to-executor"
          name="message_to_executor"
          value={localValue}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder="Dear [name]…"
          autoComplete="off"
          readOnly={readOnly}
          aria-describedby={descriptionId}
          className="w-full min-h-32 resize-vertical rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          style={{ fontFamily: FONT.sans }}
        />

        <div className="flex justify-end mt-3">
          <SectionSaveStatus
            isPending={saveMutation.isPending}
            isError={saveMutation.isError}
            lastSavedAt={lastSavedAt}
          />
        </div>
      </CardContent>
    </Card>
  );
}
